import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';

const ServiceTemplate = ({
  title,
  heroImage,
  description,
  features,
  includes,
  benefits,
  priceRange,
  duration
}) => {
  const navigate = useNavigate();

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[400px] bg-slate-900">
        <img 
          src={heroImage} 
          alt={title}
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                {title}
              </h1>
              <p className="text-xl text-slate-200 max-w-2xl">{description}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 sm:py-18 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Features */}
            <div>
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Points forts
              </h2>
              <ul className="space-y-4">
                {features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#336DBF] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Info Card */}
            <div>
              <Card className="border-slate-200/70 shadow-lg sticky top-24">
                <CardContent className="p-8 space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-[#2B2B2B] mb-4">Informations</h3>
                    <div className="space-y-3 text-sm">
                      {priceRange && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Fourchette de prix:</span>
                          <span className="font-semibold text-[#336DBF]">{priceRange}</span>
                        </div>
                      )}
                      {duration && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Durée moyenne:</span>
                          <span className="font-semibold">{duration}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate('/devis')}
                    className="w-full bg-[#336DBF] hover:bg-[#2F62AA] text-white font-medium py-6"
                  >
                    Demander un devis
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* What's Included */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Ce que comprend ce service
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {includes.map((item, i) => (
                <Card key={i} className="border-slate-200/70">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg text-[#2B2B2B] mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Benefits */}
          {benefits && benefits.length > 0 && (
            <div className="mt-16 bg-slate-50 rounded-2xl p-8 sm:p-12">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Pourquoi choisir ce service?
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#336DBF] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 sm:py-18 lg:py-24 bg-[#2B2B2B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Intéressé par ce service?
          </h2>
          <p className="text-xl text-slate-300">
            Demandez un devis personnalisé et gratuit dès maintenant.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button 
              onClick={() => navigate('/devis')}
              size="lg"
              className="bg-[#336DBF] hover:bg-[#2F62AA] text-white font-medium px-8 py-6"
            >
              Demander un devis
            </Button>
            <Button 
              onClick={() => navigate('/galerie')}
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#2B2B2B] px-8 py-6"
            >
              Voir nos réalisations
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceTemplate;